import { ContentTypeEnumSchema, MessageParticipantRole, MessageType, ResponseMessage } from '@amzn/ring-cs-chatbot-service-model';
import { ManagedSession } from './ManagedSession';

export class TypingIndicatorManager {
    private lastTypingRole?: MessageParticipantRole;
    private typingTimeout: NodeJS.Timeout | null;
    private readonly TIMEOUT_INTERVAL = 10500; // 10.5 seconds (buffer to account for slight variants in typing events being fired)
    private onTypingChangeHandlers: Set<(typingRole: MessageParticipantRole | undefined) => void> = new Set();

    constructor (private session: ManagedSession) {
        this.typingTimeout = null;

        // listen for typing and message events
        this.session.onMessage({ handler: this.handleMessage.bind(this) });
    }

    public onTypingChange (handler: (typingRole: MessageParticipantRole | undefined) => void) {
        this.onTypingChangeHandlers.add(handler);

        handler(this.lastTypingRole);

        return () => {
            this.onTypingChangeHandlers.delete(handler);
        };
    }

    private updateTypingRole (typingRole?: MessageParticipantRole) {
        if (this.lastTypingRole !== typingRole) {
            this.lastTypingRole = typingRole;
            this.onTypingChangeHandlers.forEach(handler => handler(this.lastTypingRole));
        }
    }

    private clearTypingIndicator () {
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = null;
        }
        this.updateTypingRole(undefined);
    }

    private refreshTypingTimeout () {
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }

        this.typingTimeout = setTimeout(() => {
            this.clearTypingIndicator();
        }, this.TIMEOUT_INTERVAL);
    }

    private handleMessage (message: ResponseMessage) {
        if (
            message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.typing'] &&
            message.participantRole !== MessageParticipantRole.CUSTOMER
        ) {
            this.refreshTypingTimeout();
            this.updateTypingRole(message.participantRole); // Update typing role
        }

        // clear the typing indicator if a message is sent from same participant
        if (message.participantRole === this.lastTypingRole && message.type === MessageType.MESSAGE) {
            this.clearTypingIndicator();
        }
    }
}
